import { internalLogger } from '../../../interface/v1/clientOsLogger';
import {
  ConsentState,
  SmartSharedServicesClient
} from '../clients/SmartSharedServicesClient';
import { ConsentResultType } from '../strategies/types';
import { generateOptanonConsentString } from './cookieHelpers';

/**
 * Key mappings for Smart-Shared-Services API fallback
 */
const SMART_SHARED_SERVICES_CONSENT_KEYS = {
  BASIC_FUNCTIONALITY: [
    'bizops.device.basicfunct.-.hpsmart-v1.print.npf',
    'bizops.device.basicfunct.-.hpapp.-.npf'
  ],
  ANALYTICS: [
    'prodsupport.device.telemetry.-.hpsmartv1.print.google',
    'prodsupport.device.telemetry.-.hpapp'
  ],
  ADVERTISING: [
    'advertising.device.marketing.-.hpsmart-v1.print',
    'advertising.device.telemetry.-.hpapp',
    'personalization.device.personalization.-.hpsmartv1.print'
  ]
};

/**
 * Response interface for Smart-Shared-Services consent endpoint (fallback format)
 */
export type SmartSharedServicesConsentResponse = {
  consents: {
    basicFunctionality: boolean;
    analytics: boolean;
    advertising: boolean;
  };
  timestamp: number;
  source: string;
};

/**
 * Service for mapping Smart-Shared-Services API consents to Optanon cookie format.
 * Handles the translation from API consent data to web consent format as fallback mechanism.
 */
export class SmartSharedServicesConsentMapper {
  /**
   * Get consent from Smart-Shared-Services API as fallback mechanism
   * @param client SmartSharedServicesClient instance
   * @returns Promise resolving to consent response or null on failure
   */
  public static async getFallbackConsentFromAPI(
    client: SmartSharedServicesClient
  ): Promise<SmartSharedServicesConsentResponse | null> {
    try {
      internalLogger?.debug?.(
        'SmartSharedServicesConsentMapper: Attempting Smart-Shared-Services API fallback'
      );

      // Get all purpose IDs from SMART_SHARED_SERVICES_CONSENT_KEYS
      const allPurposeIds = [
        ...SMART_SHARED_SERVICES_CONSENT_KEYS.BASIC_FUNCTIONALITY,
        ...SMART_SHARED_SERVICES_CONSENT_KEYS.ANALYTICS
        // For now, advertising won't be used.
        //  ...SMART_SHARED_SERVICES_CONSENT_KEYS.ADVERTISING
      ];

      const response = await client.getConsentStatesByPurposeIds(allPurposeIds);

      if (response) {
        // Convert API response to SmartSharedServicesConsentResponse format
        const convertedResponse =
          this.convertAPIResponseToConsentResponse(response);

        internalLogger?.debug?.(
          'SmartSharedServicesConsentMapper: Successfully retrieved consents from Smart-Shared-Services API',
          convertedResponse
        );
        return convertedResponse;
      }

      internalLogger?.warn(
        'SmartSharedServicesConsentMapper: Smart-Shared-Services API returned null'
      );
      return null;
    } catch (error) {
      internalLogger?.error(
        'SmartSharedServicesConsentMapper: Smart-Shared-Services API fallback failed:',
        error
      );
      return null;
    }
  }

  /**
   * Convert API response to SmartSharedServicesConsentResponse format
   * @param apiResponse Array of consent states by purpose ID
   * @returns Formatted consent response
   */
  public static convertAPIResponseToConsentResponse(
    apiResponse: Array<{ purposeId: string; state: ConsentState }>
  ): SmartSharedServicesConsentResponse {
    // Check consent state for each category
    const hasBasicFunctionalityConsent = apiResponse.some(
      (consent) =>
        SMART_SHARED_SERVICES_CONSENT_KEYS.BASIC_FUNCTIONALITY.includes(
          consent.purposeId
        ) && consent.state === 'OptedIn'
    );

    const hasAnalyticsConsent = apiResponse.some(
      (consent) =>
        SMART_SHARED_SERVICES_CONSENT_KEYS.ANALYTICS.includes(
          consent.purposeId
        ) && consent.state === 'OptedIn'
    );

    /*
    /// 3. Advertising: Check if any advertising keys are true
    /// For now, wont be used.
    const hasAdvertisingConsent = apiResponse.some(
      (consent) =>
        SMART_SHARED_SERVICES_CONSENT_KEYS.ADVERTISING.includes(
          consent.purposeId
        ) && consent.state === 'OptedIn'
    );
    */
    const hasAdvertisingConsent = false;

    return {
      consents: {
        //TODO: Currently, we are applying the same consent to basic functionality and analytics.
        basicFunctionality: hasAnalyticsConsent, //hasBasicFunctionalityConsent,
        analytics: hasAnalyticsConsent,
        advertising: hasAdvertisingConsent
      },
      timestamp: Date.now(),
      source: 'smart-shared-services'
    };
  }

  /**
   * Convert Smart-Shared-Services API response to Optanon format
   * @param apiResponse Smart-Shared-Services consent response
   * @returns ConsentResultType with Optanon format
   */
  public static convertAPIResponseToOptanon(
    apiResponse: SmartSharedServicesConsentResponse
  ): ConsentResultType {
    try {
      const { consents } = apiResponse;

      // Convert API format to groups object for helper function
      const groupsObject: Record<string, 0 | 1> = {
        '1': consents.basicFunctionality ? 1 : 0, // Basic functionality
        '2': consents.analytics ? 1 : 0, // Analytics
        '3': consents.advertising ? 1 : 0, // Functional/Advertising
        '4': consents.advertising ? 1 : 0 // Targeting/Advertising
      };

      const optanonConsent = generateOptanonConsentString({
        groups: groupsObject
      });

      internalLogger?.debug?.(
        'SmartSharedServicesConsentMapper: Successfully converted API response to Optanon format',
        {
          consents,
          groupsObject,
          optanonConsent
        }
      );

      return {
        optanonConsent,
        hasError: false
      };
    } catch (error) {
      internalLogger?.error(
        'SmartSharedServicesConsentMapper: Failed to convert API response:',
        error
      );
      return {
        optanonConsent: '',
        hasError: true,
        errorMessage: `API response conversion failed: ${error}`
      };
    }
  }
}
